import { Fragment } from 'react';

/**
 * A cadeia de praças do MULTI, a mesma régua nas pills (shell e takeover), no
 * resumo compacto do celular e no título do explorer (#4003). A cadeia só
 * continua quando o trecho embarca onde o anterior pousou; um traslado por
 * terra (GRU → REC, depois SSA → CNF) QUEBRA a cadeia em `GRU → REC · SSA → CNF`.
 * Montar `[origem do 1º, …destinos]` apagava o SSA e desenhava um voo REC → CNF
 * que ninguém pediu.
 */

type LegEnds = Readonly<{ origin: string; destination: string }>;

/** As corridas contínuas de praças: cada uma é uma cadeia `A → B → C`, e uma
 *  corrida nova começa no trecho cuja origem não é o destino do anterior. */
export function legRuns(legs: ReadonlyArray<LegEnds>): string[][] {
  const runs: string[][] = [];
  for (const leg of legs) {
    const run = runs.at(-1);
    if (run?.at(-1) === leg.origin) run.push(leg.destination);
    else runs.push([leg.origin, leg.destination]);
  }
  return runs;
}

/** A cadeia em texto: `GRU → REC → SSA`, ou `GRU → REC · SSA → CNF` com quebra.
 *  `label` troca o IATA pelo nome longo do nome acessível (IATA + cidade). */
export function legChainText(
  legs: ReadonlyArray<LegEnds>,
  label: (iata: string) => string = (iata) => iata,
): string {
  return legRuns(legs)
    .map((run) => run.map(label).join(' → '))
    .join(' · ');
}

/** A cadeia em linha, com os IATAs em peso e seta e quebra em tom de apoio,
 *  dentro de um `<span>` de fluxo INLINE: os espaços entre eles são text nodes,
 *  e num contêiner flex text node só de espaço é descartado — a cadeia colaria
 *  e o nome acessível junto. */
/** Seta e quebra: presentes sempre, em tom de apoio. */
const DIM_CLASS = 'text-muted-foreground font-normal';

export function LegChain({ legs }: Readonly<{ legs: ReadonlyArray<LegEnds> }>) {
  return (
    <span className="inline">
      {legRuns(legs).map((run, r) => (
        <Fragment key={`run-${String(r)}`}>
          {r > 0 && <span className={DIM_CLASS}> · </span>}
          {run.map((iata, i) => (
            <Fragment key={`${iata}-${String(i)}`}>
              {i > 0 && (
                <>
                  {' '}
                  <span className={DIM_CLASS}>→</span>{' '}
                </>
              )}
              <span className="font-semibold">{iata}</span>
            </Fragment>
          ))}
        </Fragment>
      ))}
    </span>
  );
}
